import React, { useEffect } from 'react';
import { Button, DatePicker, Form, Select, Spin, TimePicker } from 'antd';
import { useTranslation } from 'react-i18next';
import TagInput from '../Elements/TagInput';
import { DATE_BACKEND_FORMAT, DAY_FORMAT, TIME_FORMAT } from '../../constants';
import isEmpty from 'lodash/isEmpty';
import dayjs from 'dayjs';
import AddressInput from '../Inputs/AddressInput';

const initShift = {
  start: null,
  end: null,
  driverId: null,
  date: null,
  skills: [],
  fleetId: null,
  startAddress: {},
  endAddress: {},
};

const ShiftForm = ({ handleSubmit, handleDelete, action, shift, handleCancel, drivers, fleets, isFetching }) => {
  const [form] = Form.useForm();
  const { t } = useTranslation('scheduler');
  const { t: tc } = useTranslation('common');

  const [skills, setSkills] = React.useState([]);

  const setParams = (values) => {
    const params = {
      ...values,
      start: values?.start?.format(TIME_FORMAT),
      end: values?.end?.format(TIME_FORMAT),
      date: values?.date.format(DATE_BACKEND_FORMAT),
      skills: skills,
    };
    params.startAddress = values.startAddress?.address;
    params.startAddressLat = values.startAddress?.latLng?.lat;
    params.startAddressLng = values.startAddress?.latLng?.lng;
    params.endAddress = values.endAddress?.address || null;
    params.endAddressLat = values.endAddress?.latLng?.lat || null;
    params.endAddressLng = values.endAddress?.latLng?.lng || null;
    return params;
  };
  const onFinishForm = (values) => {
    handleSubmit && handleSubmit(setParams(values));
  };

  useEffect(() => {
    if (action === 'new') {
      const driver = drivers.find((d) => d.id == shift?.driverId);
      form.setFieldsValue({
        ...initShift,
        date: shift?.date ? dayjs(shift?.date, DATE_BACKEND_FORMAT) : null,
        driverId: shift?.driverId,
        skills: driver?.skills || [],
      });
      setSkills(driver?.skills || []);
    } else if (shift) {
      form.setFieldsValue({
        ...shift,
        start: dayjs(shift?.start, TIME_FORMAT),
        end: dayjs(shift?.end, TIME_FORMAT),
        date: dayjs(shift?.date, DATE_BACKEND_FORMAT),
        startAddress: {
          address: shift.startAddress,
          latLng: {
            lat: shift.startAddressLat,
            lng: shift.startAddressLng,
          },
        },
        endAddress: {
          address: shift.endAddress,
          latLng: {
            lat: shift.endAddressLat,
            lng: shift.endAddressLng,
          },
        },
      });
      setSkills(shift.skills || []);
    }
  }, [action, shift]);

  const resetErrorTime = () => {
    form.setFields([
      {
        name: 'start',
        errors: [],
      },
      {
        name: 'end',
        errors: [],
      },
    ]);
  };

  const handleOnChangeStart = (value) => {
    form.setFieldsValue({ start: value });
    if (isEmpty(value) || isEmpty(form.getFieldValue('end'))) {
      resetErrorTime();
      return;
    }
    if (form.getFieldValue('end') < value) {
      form.setFields([
        {
          name: 'start',
          errors: [t('shiftEndError')],
        },
      ]);
    }
  };

  const handleOnChangeEnd = (value) => {
    form.setFieldsValue({ end: value });
    if (isEmpty(value) || isEmpty(form.getFieldValue('start'))) {
      resetErrorTime();
      return;
    }
    if (form.getFieldValue('start') > value) {
      form.setFields([
        {
          name: 'end',
          errors: [t('shiftStartError')],
        },
      ]);
    }
  };

  const handleChangeDriver = (id) => {
    const driver = drivers.find((d) => d.id === id);
    form.setFieldsValue({ skills: driver.skills || [] });
    setSkills(driver.skills || []);
  };

  const onValuesChange = (changedFields, allFields) => {
    if (changedFields.fleetId) {
      const fleet = fleets.find((f) => f.id === allFields.fleetId);
      form.setFieldsValue({
        startAddress: {
          address: fleet.hub.startAddress,
          latLng: {
            lat: fleet.hub.startAddressLat,
            lng: fleet.hub.startAddressLng,
          },
        },
        endAddress: {
          address: fleet.hub.startAddress,
          latLng: {
            lat: fleet.hub.startAddressLat,
            lng: fleet.hub.startAddressLng,
          },
        },
      });
    }
  };

  return (
    <Spin spinning={isFetching}>
      <div className="ShiftForm">
        <div className="FormHeader"></div>
        <div className="FormContent">
          <Form
            form={form}
            scrollToFirstError
            layout="vertical"
            requiredMark={false}
            colon={false}
            onFinish={onFinishForm}
            onValuesChange={onValuesChange}
          >
            <Form.Item label={t('date')} name="date" rules={[{ required: true, message: tc('required') }]}>
              <DatePicker placeholder={t('date')} format={DAY_FORMAT} style={{ width: '100%' }} />
            </Form.Item>

            <Form.Item label={t('start')} name="start">
              <TimePicker
                placeholder={t('start')}
                format={TIME_FORMAT}
                style={{ width: '100%' }}
                onChange={handleOnChangeStart}
                onSelect={handleOnChangeStart}
              />
            </Form.Item>

            <Form.Item label={t('end')} name="end">
              <TimePicker
                placeholder={t('end')}
                format={TIME_FORMAT}
                style={{ width: '100%' }}
                onChange={handleOnChangeEnd}
                onSelect={handleOnChangeEnd}
              />
            </Form.Item>

            <Form.Item label={t('driver')} name="driverId" rules={[{ required: true, message: tc('required') }]}>
              <Select
                placeholder={t('selectDriver')}
                options={drivers.map((driver) => ({
                  value: driver.id,
                  label: driver.name,
                }))}
                onChange={handleChangeDriver}
              />
            </Form.Item>

            <Form.Item label={tc('type')} name="fleetId" rules={[{ required: true, message: tc('required') }]}>
              <Select
                placeholder={t('selectFleet')}
                options={fleets.map((fleet) => ({
                  value: fleet.id,
                  label: fleet.name,
                }))}
              />
            </Form.Item>

            <Form.Item
              label={t('startAddress')}
              name="startAddress"
              rules={[
                {
                  validator: (_, value) => {
                    if (value?.address && value?.latLng?.lat && value?.latLng?.lng) {
                      return Promise.resolve();
                    }
                    if (isEmpty(value?.address)) {
                      return Promise.reject(new Error(tc('required')));
                    }
                    return Promise.reject(new Error(tc('invalid')));
                  },
                },
              ]}
            >
              <AddressInput
                placeholder={t('startAddress')}
                onSelect={(value) => form.setFieldsValue({ startAddress: value })}
              />
            </Form.Item>

            <Form.Item
              label={t('endAddress')}
              name="endAddress"
              rules={[
                {
                  validator: (_, value) => {
                    if (isEmpty(value?.address)) {
                      return Promise.resolve();
                    }
                    if (value?.address && value?.latLng?.lat && value?.latLng?.lng) {
                      return Promise.resolve();
                    }
                    return Promise.reject(new Error(tc('invalid')));
                  },
                },
              ]}
            >
              <AddressInput
                placeholder={t('endAddress')}
                onSelect={(value) => form.setFieldsValue({ endAddress: value })}
              />
            </Form.Item>

            <Form.Item label={t('skills')} name="skills">
              <TagInput tags={skills} onChange={(values) => setSkills(values)} />
            </Form.Item>
          </Form>
        </div>
        <div className="FormFooter">
          <Button key="cancel" onClick={handleCancel}>
            {tc('cancel')}
          </Button>
          {shift?.id && (
            <Button type="primary" danger block size="large" onClick={handleDelete}>
              {tc('delete')}
            </Button>
          )}
          <Button type="primary" block size="large" onClick={() => form.submit()}>
            {action === 'new' ? tc('create') : tc('update')}
          </Button>
        </div>
      </div>
    </Spin>
  );
};

export default ShiftForm;
